import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, RefreshCw, UserPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { api, API, confidenceClass, confidenceLabel } from "@/lib/api";

const FIELD_GROUPS = [
  { title: "Dados Pessoais", keys: [
    ["nome_completo", "Nome completo"], ["cpf", "CPF"], ["rg", "RG"],
    ["rg_orgao", "Órgão Expedidor"], ["rg_uf", "UF do RG"], ["cnh", "CNH"],
    ["data_nascimento", "Data de nascimento"], ["nome_mae", "Nome da mãe"],
    ["nome_pai", "Nome do pai"], ["naturalidade", "Naturalidade"], ["nacionalidade", "Nacionalidade"],
    ["telefone", "Telefone"], ["whatsapp", "WhatsApp"], ["email", "E-mail"],
  ]},
  { title: "Endereço (do comprovante)", keys: [
    ["endereco_logradouro","Logradouro"], ["endereco_numero","Número"],
    ["endereco_complemento","Complemento"], ["endereco_bairro","Bairro"],
    ["endereco_cidade","Cidade"], ["endereco_uf","UF"], ["endereco_cep","CEP"],
  ]},
];

function PreviewPanel({ previews }) {
  const [idx, setIdx] = useState(0);
  const [blob, setBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    if (!previews?.length) return;
    setLoading(true);
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
    const p = previews[idx];
    api.get(`/ocr/extractions/${p.id}/file`, { responseType: "blob" })
      .then(r => { const url = URL.createObjectURL(r.data); objectUrlRef.current = url; setBlob({ url, type: r.data.type, name: p.name, doctype: p.type }); })
      .catch(() => setBlob(null))
      .finally(() => setLoading(false));
    return () => { if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; } };
  }, [idx, previews]);

  if (!previews?.length) return null;
  const isPdf = blob?.type?.includes("pdf") || blob?.name?.toLowerCase().endsWith(".pdf");

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 sticky top-6" data-testid="preview-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium text-slate-600 truncate">{blob?.name || "Documento"} {blob?.doctype ? <span className="text-slate-400">· {blob.doctype}</span> : null}</div>
        {previews.length > 1 && (
          <div className="flex items-center gap-1">
            <button onClick={() => setIdx((idx - 1 + previews.length) % previews.length)} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="prev-preview"><ChevronLeft className="w-4 h-4"/></button>
            <span className="text-xs text-slate-500 tabular-nums">{idx+1}/{previews.length}</span>
            <button onClick={() => setIdx((idx + 1) % previews.length)} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="next-preview"><ChevronRight className="w-4 h-4"/></button>
          </div>
        )}
      </div>
      <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-100 min-h-[300px] flex items-center justify-center">
        {loading ? (
          <div className="text-xs text-slate-500 p-6">Carregando preview...</div>
        ) : !blob ? (
          <div className="text-xs text-slate-500 p-6">Preview indisponível</div>
        ) : isPdf ? (
          <iframe title="doc" src={blob.url} className="w-full h-[520px] rounded-lg"/>
        ) : (
          <img src={blob.url} alt="documento" className="w-full h-auto max-h-[70vh] object-contain"/>
        )}
      </div>
    </div>
  );
}

export default function OCRReview() {
  const navigate = useNavigate();
  const [data, setData] = useState({ fields: {}, ids: [], previews: [], document_types: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("ocr_review");
    if (!raw) { navigate("/reader"); return; }
    setData(JSON.parse(raw));
  }, []);

  const valueOf = (k) => data.fields?.[k]?.value || "";
  const confOf = (k) => data.fields?.[k]?.confidence;

  const setValue = (k, v) => {
    setData(d => ({ ...d, fields: { ...d.fields, [k]: { ...(d.fields[k]||{}), value: v, confidence: d.fields[k]?.confidence ?? 1 } } }));
  };

  const lowConfCount = useMemo(() => Object.values(data.fields||{}).filter(f => (f.confidence||1) < 0.8).length, [data]);

  const confirm = async () => {
    setSaving(true);
    try {
      const payload = {};
      const confObj = {};
      for (const [k, obj] of Object.entries(data.fields || {})) {
        payload[k] = obj.value || "";
        confObj[k] = obj.confidence ?? null;
      }
      payload.ocr_confidence = confObj;
      payload.source_docs = data.ids || [];
      const r = await api.post("/clients", payload);
      sessionStorage.removeItem("ocr_review");
      if (r.data.duplicate) {
        if (window.confirm("Este cliente já está cadastrado. Deseja atualizar os dados?")) {
          await api.put(`/clients/${r.data.client.id}`, payload);
          toast.success("Cliente atualizado");
          navigate(`/clients/${r.data.client.id}/new-document`);
          return;
        }
        navigate(`/clients/${r.data.client.id}`);
      } else {
        toast.success("Cliente cadastrado");
        navigate(`/clients/${r.data.client.id}/new-document`);
      }
    } catch (e) {
      toast.error("Erro ao cadastrar cliente");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-7xl mx-auto w-full">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Confira os dados identificados</h1>
          <p className="text-slate-500 mt-1">Compare com o documento original e corrija o que for necessário.</p>
        </div>
        <button onClick={() => navigate("/reader")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-2" data-testid="btn-reread">
          <RefreshCw className="w-4 h-4" /> Ler novamente
        </button>
      </div>

      {lowConfCount > 0 && (
        <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div><b>{lowConfCount} campo(s)</b> com baixa confiança do OCR — revise antes de continuar.</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 order-2 lg:order-1">
          <PreviewPanel previews={data.previews} />
        </div>
        <div className="lg:col-span-3 order-1 lg:order-2 space-y-6">
          {FIELD_GROUPS.map(group => (
            <section key={group.title} className="bg-white rounded-2xl border border-slate-100 p-6">
              <h2 className="font-heading text-lg font-semibold mb-4">{group.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {group.keys.map(([k, label]) => (
                  <label key={k} className="block">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">{label}
                        {["whatsapp","email","telefone"].includes(k) && <span className="ml-1 text-[10px] text-[#0f4c5c]">(contato)</span>}
                      </span>
                      {confOf(k) != null && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${confidenceClass(confOf(k))}`}>
                          Confiança {confidenceLabel(confOf(k))}
                        </span>
                      )}
                    </div>
                    <input
                      data-testid={`field-${k}`}
                      value={valueOf(k)} onChange={(e) => setValue(k, e.target.value)}
                      placeholder={["whatsapp","email"].includes(k) ? "Adicione se quiser cadastrar" : "Não identificado"}
                      className={`w-full h-10 px-3 rounded-lg bg-slate-50 border ${(confOf(k)||1)<0.7 ? 'border-red-200' : 'border-slate-200'} focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none text-sm`}
                    />
                  </label>
                ))}
              </div>
            </section>
          ))}
          <section className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-heading text-lg font-semibold mb-4">Classificação</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-xs font-medium text-slate-600 mb-1">Área do direito</div>
                <select value={valueOf("area") || "outros"} onChange={(e) => setValue("area", e.target.value)} data-testid="field-area"
                  className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                  <option value="civil">Direito Civil</option>
                  <option value="tributario">Direito Tributário</option>
                  <option value="previdenciario">Direito Previdenciário</option>
                  <option value="penal">Direito Penal</option>
                  <option value="familia">Direito de Família</option>
                  <option value="outros">Outros</option>
                </select>
              </label>
              <label className="block">
                <div className="text-xs font-medium text-slate-600 mb-1">Status do atendimento</div>
                <select value={valueOf("status") || "nao_concluido"} onChange={(e) => setValue("status", e.target.value)} data-testid="field-status"
                  className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                  <option value="nao_concluido">Não concluído</option>
                  <option value="concluido">Concluído</option>
                </select>
              </label>
            </div>
            <p className="text-xs text-slate-500 mt-3">Quer adicionar WhatsApp/e-mail do cliente? Preencha nos campos acima — ficam salvos no cadastro.</p>
          </section>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-end gap-3">
        <button data-testid="btn-confirm-ocr" onClick={confirm} disabled={saving}
          className="gradient-primary text-white px-6 h-11 rounded-xl inline-flex items-center gap-2 hover:-translate-y-0.5 transition text-sm font-medium">
          <UserPlus className="w-4 h-4" /> {saving ? "Cadastrando..." : "Confirmar e cadastrar cliente"}
        </button>
      </div>
    </div>
  );
}
